import * as React from "react";
import { Link } from "react-router";
import { Search } from "lucide-react";

import Medallion from "@/components/common/Medallion";
import SurahName from "@/components/common/SurahName";
import type { Surah } from "@/lib/types";

interface SurahsViewProps {
	surahs: Surah[];
}

/**
 * Lower-case, strip diacritics (č ć š ž and Arabic harakat are combining marks
 * after NFD; đ is not, so it becomes "dj") and drop punctuation and spaces, so
 * "dzin", "Dž in" and "Džinni" or "al fatiha" and "Al-Fatihah" match.
 */
const normalize = (value: string) =>
	value
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{M}/gu, "")
		.replace(/đ/g, "dj")
		.replace(/[^\p{L}\p{N}]/gu, "");

interface SurahItem {
	surah: Surah;
	pageFrom: number | null;
	pageTo: number | null;
	haystack: string;
}

const SurahsView: React.FC<SurahsViewProps> = ({ surahs }) => {
	const [query, setQuery] = React.useState("");
	const deferredQuery = React.useDeferredValue(query);

	const items = React.useMemo<SurahItem[]>(
		() =>
			[...surahs]
				.sort((a, b) => a.number - b.number)
				.map((surah) => {
					// [int, int] is stored as a string (sqlite has no arrays)
					let pages: unknown = null;
					try {
						pages = JSON.parse(surah.pages);
					} catch {
						pages = null;
					}
					const [pageFrom = null, pageTo = null] = Array.isArray(pages)
						? (pages as number[])
						: [];
					return {
						surah,
						pageFrom,
						pageTo,
						haystack: [surah.name_simple, surah.name_bosnian, surah.name_arabic]
							.map((name) => normalize(name ?? ""))
							.join(" "),
					};
				}),
		[surahs],
	);

	const visible = React.useMemo(() => {
		const tokens = deferredQuery.split(/\s+/).map(normalize).filter(Boolean);
		if (tokens.length === 0) return items;
		return items.filter(({ surah, haystack }) =>
			tokens.every((token) =>
				/^\d+$/.test(token)
					? String(surah.number).startsWith(token)
					: haystack.includes(token),
			),
		);
	}, [items, deferredQuery]);

	return (
		<section className="space-y-4 py-6">
			<div role="search" className="relative sm:max-w-sm">
				<Search
					aria-hidden
					className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
				/>
				<input
					type="search"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder="Pretraži sure"
					aria-label="Pretraži sure"
					autoComplete="off"
					autoCorrect="off"
					spellCheck={false}
					enterKeyHint="search"
					className="h-11 w-full rounded-control border-paper-300 bg-paper-50 pl-10 pr-4 text-base text-ink shadow-card placeholder:text-ink-subtle/80 focus:border-emerald-700 focus:ring-4 focus:ring-emerald-700/15 sm:text-sm"
				/>
				<p role="status" className="sr-only">
					{deferredQuery.trim() ? `Pronađeno sura: ${visible.length}` : ""}
				</p>
			</div>

			{visible.length > 0 ? (
				<ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{visible.map(({ surah, pageFrom, pageTo }) => (
						<li key={surah.id} className="min-w-0">
							<Link
								to={pageFrom ? `/${pageFrom}` : "/"}
								className="pressable group block h-full rounded-card bg-paper-50 p-4 shadow-card ring-1 ring-paper-300 transition-shadow [--press-scale:0.985] hover:shadow-card-hover hover:ring-gold-400/60"
							>
								<div className="flex items-center gap-3.5">
									<Medallion>{surah.number}</Medallion>
									<div className="min-w-0 flex-1">
										<h2 className="truncate text-[15px] font-semibold text-ink">
											{surah.name_simple}
										</h2>
										<p className="truncate text-sm text-ink-muted">{surah.name_bosnian}</p>
									</div>
									<SurahName
										number={surah.number}
										nameArabic={surah.name_arabic}
										className="shrink-0 text-[40px]"
									/>
								</div>
								<ul className="mt-3 flex flex-wrap gap-1.5 text-[11px] font-medium text-ink-muted">
									<li className="rounded-full bg-paper-200/70 px-2 py-0.5">
										{surah.revelation_place === "makkah" ? "Mekka" : "Medina"}
									</li>
									<li className="rounded-full bg-paper-200/70 px-2 py-0.5 tabular-nums">
										Redoslijed objave: {surah.revelation_order}
									</li>
									{pageFrom ? (
										<li className="rounded-full bg-paper-200/70 px-2 py-0.5 tabular-nums">
											Stranice: {pageFrom}–{pageTo ?? pageFrom}
										</li>
									) : null}
								</ul>
							</Link>
						</li>
					))}
				</ul>
			) : (
				<p className="rounded-card bg-paper-100 px-4 py-6 text-center text-sm text-ink-muted ring-1 ring-paper-300">
					Nijedna sura ne odgovara pretrazi „{deferredQuery.trim()}“.
				</p>
			)}
		</section>
	);
};

export default React.memo(SurahsView);
